import { useState } from "react";
import { Box, Button, Typography, Alert } from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Link as RouterLink } from "react-router-dom";
import useCreateAsset from "hooks/assets/useCreateAsset";
import useLocations from "hooks/locations/useLocations";
import useEmployees from "hooks/employees/useEmployees";
import { Asset } from "types/asset";
import { formSurfaceSx } from "constants/pageSurfaces";
import AssetForm, {
  costFromFormForCreate,
  emptyAssetFormValues,
  AssetFormValues,
} from "./AssetForm";

function formValuesToCreatePayload(values: AssetFormValues): Partial<Asset> {
  return {
    uuid: "",
    id: 0,
    state: "active",
    status: values.status || "Available",
    serialNumber: values.serialNumber,
    unitNumber: values.unitNumber,
    assetTag: values.assetTag,
    model: values.model,
    manufacturer: values.manufacturer,
    vendor: "",
    accessories: [],
    purchaseDate: values.purchaseDate,
    expirationDate: "",
    endOfLifeDate: "",
    deviceType: values.deviceType,
    condition: values.condition,
    quantity: values.quantity ? Number(values.quantity) : undefined,
    notes: values.notes,
    warrantyExpiresOn: values.warrantyExpiresOn,
    locationId: values.locationId || undefined,
    employeeId: values.employeeId ? Number(values.employeeId) : null,
    ...costFromFormForCreate(values.cost),
  };
}

export default function AssetNew() {
  const { create } = useCreateAsset();
  const { locations } = useLocations();
  const { employees } = useEmployees();
  const [values, setValues] = useState<AssetFormValues>(emptyAssetFormValues());
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    create(formValuesToCreatePayload(values)).catch((err) => {
      const msg =
        err?.response?.data?.errors?.join?.(", ") || "Could not create asset";
      setError(String(msg));
    });
  };

  return (
    <div>
      <Box>
        <Breadcrumbs
          data={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Assets", to: "/assets" },
            { label: "New asset" },
          ]}
        />
      </Box>
      <Box sx={formSurfaceSx}>
        <Typography variant="h4" mb={1} fontWeight="bold">
          Register asset
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Same fields as edit — set everything now or come back later from the list.
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Box component="form" onSubmit={handleSubmit}>
          <AssetForm
            values={values}
            setValues={setValues}
            locations={locations}
            employees={employees}
          />
          <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button type="submit" variant="contained">
              Create
            </Button>
            <Button component={RouterLink} to="/assets" variant="outlined">
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
